import { athleteCounterContext } from "../../../custom-disciplines/athlete-counter.js";
import {
  isTimerAction,
  timerCommandFromPorts,
  timerScope,
} from "../../../behavior-timers/protocol.js";
import { EditorStyleActionKey } from "../../index.js";
import {
  ACTION_SET_KEY_VALUE_TYPE,
  ActionSetNode,
  BindingExpression,
  NodePortValue,
} from "../../types.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
} from "../types.js";
import { emptyPatchResult, ensureBindingExpression } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class ActionBehaviorNode extends BehaviorNode {
  declare behavior: ActionSetNode;

  constructor(behavior: ActionSetNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const visiting: Set<string> = new Set();
    const patch: PatchResult = emptyPatchResult();

    if (isTimerAction(this.behavior.key)) {
      patch.componentPatch = {
        behaviorTimerCommands: [
          timerCommandFromPorts(
            this.behavior.key,
            Object.fromEntries(
              ["reference", "duration", "unit", "mode"].map((key) => [
                key,
                this.getInput(key, visiting, context),
              ]),
            ),
            timerScope(context.data),
          ),
        ],
      };
      return this.finishFlow(patch, context);
    }
    if (this.behavior.key === "customIncrementAthleteCount") {
      patch.componentPatch = {
        customIncrementAthleteCount: athleteCounterContext(context.data),
      };
      return this.finishFlow(patch, context);
    }
    if (this.behavior.key === "customGroupService") {
      patch.componentPatch = {
        customGroupService: Object.fromEntries(
          [
            "service",
            "groupId",
            "fieldMap",
            "format",
            "athleteId",
            "options",
          ].map((key) => [key, this.getInput(key, visiting, context)]),
        ),
      };
      return this.finishFlow(patch, context);
    }
    if (this.behavior.key === "customSubmitForm") {
      patch.componentPatch = { customSubmitForm: {} };
      return this.finishFlow(patch, context);
    }
    if (this.behavior.key === "customRunAction") {
      patch.componentPatch = {
        customRunAction: {
          actionKey: this.getInput("actionKey", visiting, context),
          contextAthleteId: this.getInput(
            "contextAthleteId",
            visiting,
            context,
          ),
          contextColumnKey: this.getInput(
            "contextColumnKey",
            visiting,
            context,
          ),
        },
      };
      return this.finishFlow(patch, context);
    }
    if (this.behavior.key === "customPrintModal") {
      patch.componentPatch = {
        customPrintModal: {
          modalKey: this.getInput("modalKey", visiting, context),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "setFilter") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = { listFilterPredicate: expression };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "setSort") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = { listSortComparator: expression };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "setListSource") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = { listSource: expression };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "setBinding") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.bindingPatch = expression;
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "totalWeight") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = { totalWeight: expression };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "barWeight") {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = { barWeight: expression };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customListSetField") {
      const list = this.getInput("list", visiting, context);
      const searchKeyRaw = this.getInput("searchKey", visiting, context);
      const searchValueRaw = this.getInput("searchValue", visiting, context);
      const fieldKeyRaw = this.getInput("fieldKey", visiting, context);
      const nextValue = this.getInput("nextValue", visiting, context);
      patch.componentPatch = {
        customListSetField: {
          list,
          searchKey: String(searchKeyRaw ?? ""),
          searchValue: String(searchValueRaw ?? ""),
          fieldKey: String(fieldKeyRaw ?? ""),
          nextValue,
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customListSetRows") {
      const list = this.getInput("list", visiting, context);
      const mode = this.getInput("mode", visiting, context);
      const targetGroupId = this.getInput("targetGroupId", visiting, context);
      const persistFields = this.getInput("persistFields", visiting, context);
      const deleteMissing = this.getInput("deleteMissing", visiting, context);
      patch.componentPatch = {
        customListSetRows: {
          list,
          mode: String(mode ?? ""),
          targetGroupId: String(targetGroupId ?? ""),
          persistFields,
          deleteMissing,
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customListSetViewRows") {
      const list = this.getInput("list", visiting, context);
      patch.componentPatch = {
        customListSetViewRows: {
          list,
          includeNewRows: this.getInput("includeNewRows", visiting, context),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customGroupSetField") {
      const groupId = this.getInput("groupId", visiting, context);
      const fieldKey = this.getInput("fieldKey", visiting, context);
      const nextValue = this.getInput("nextValue", visiting, context);
      patch.componentPatch = {
        customGroupSetField: {
          groupId: String(groupId ?? ""),
          fieldKey: String(fieldKey ?? ""),
          nextValue,
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customOpenGroup") {
      const groupId = this.getInput("groupId", visiting, context);
      patch.componentPatch = {
        customOpenGroup: {
          groupId: String(groupId ?? ""),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customSetCurrentGroup") {
      const groupId = this.getInput("groupId", visiting, context);
      patch.componentPatch = {
        customSetCurrentGroup: {
          groupId: String(groupId ?? "").trim(),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customSetCurrentAthlete") {
      const athleteIdInput = this.getInput("athleteId", visiting, context);
      let athleteId: string;
      if (athleteIdInput === null || athleteIdInput === undefined) {
        const contextAthlete = context.data.athlete;
        const contextAthleteId =
          contextAthlete && typeof contextAthlete === "object"
            ? (contextAthlete as { id?: unknown }).id
            : undefined;
        athleteId =
          String(contextAthleteId ?? "").trim() ||
          String(context.data.selectedRowId ?? "").trim();
      } else {
        // Explicit empty string clears the current athlete.
        athleteId = String(athleteIdInput).trim();
      }
      patch.componentPatch = {
        customSetCurrentAthlete: {
          athleteId,
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customOpenModal") {
      const modalKey = this.getInput("modalKey", visiting, context);
      const contextAthleteId = this.getInput(
        "contextAthleteId",
        visiting,
        context,
      );
      const contextGroupId = this.getInput("contextGroupId", visiting, context);
      patch.componentPatch = {
        customOpenModal: {
          modalKey: String(modalKey ?? ""),
          contextAthleteId: String(contextAthleteId ?? ""),
          contextGroupId: String(contextGroupId ?? ""),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customCloseModal") {
      const modalKey = this.getInput("modalKey", visiting, context);
      patch.componentPatch = {
        customCloseModal: {
          modalKey: String(modalKey ?? ""),
        },
      };
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "customInputOptions") {
      const options = this.getInput("value", visiting, context);
      patch.componentPatch = {
        customInputOptions: options,
      };
      return this.finishFlow(patch, context);
    }

    if (
      this.behavior.key === "customEntryEligible" ||
      this.behavior.key === "customEntryMinimum" ||
      this.behavior.key === "customEntryMaximum" ||
      this.behavior.key === "customEntryStep" ||
      this.behavior.key === "customEntryAllowWhileCalled" ||
      this.behavior.key === "customEntryUnlimitedChanges" ||
      this.behavior.key === "customEntryReviewChanges" ||
      this.behavior.key === "customEntryMaxChanges" ||
      this.behavior.key === "customEntryTicketReference" ||
      this.behavior.key === "customActionDisabled" ||
      this.behavior.key === "customActionLabel" ||
      this.behavior.key === "customActionIcon" ||
      this.behavior.key === "customActionStartIcon" ||
      this.behavior.key === "customActionVariant" ||
      this.behavior.key === "customActionSize" ||
      this.behavior.key === "customActionColor" ||
      this.behavior.key === "customActionTooltip"
    ) {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = {
          ...patch.componentBindingPatch,
          [this.behavior.key]: expression,
        };
      }
      return this.finishFlow(patch, context);
    }

    const targetRaw = this.getInput("target", visiting, context);
    const target = this.toNodeTarget(targetRaw);

    if (
      target?.component === "barLoader" &&
      this.behavior.key !== "location" &&
      this.behavior.key !== "transform"
    ) {
      const expression = ensureBindingExpression(
        this.getInput("value", visiting, context, true),
      );
      if (expression) {
        patch.componentBindingPatch = {
          barLoaderTargetBindingPatches: [
            {
              target,
              key: this.behavior.key,
              expression,
            },
          ],
        };
      }
      return this.finishFlow(patch, context);
    }

    if (this.behavior.key === "text") {
      const raw = this.getInput("value", visiting, context);
      patch.textPatch = raw == null ? "" : String(raw);
    } else if (this.behavior.key === "location") {
      const locationBindingPatch = this.resolveCompositeBindingPatch(
        "value",
        ["x", "y", "z"],
        visiting,
        context,
      );
      if (locationBindingPatch) {
        patch.locationBindingPatch = locationBindingPatch;
      }
    } else if (this.behavior.key === "transform") {
      const transformBindingPatch = this.resolveCompositeBindingPatch(
        "value",
        ["width", "height", "rotation"],
        visiting,
        context,
      );
      if (transformBindingPatch) {
        patch.transformBindingPatch = transformBindingPatch;
      }
    } else {
      const styleKey = this.behavior.key as EditorStyleActionKey;
      if (ACTION_SET_KEY_VALUE_TYPE[styleKey]) {
        const expression = ensureBindingExpression(
          this.getInput("value", visiting, context, true),
        );
        if (expression) {
          patch.styleBindingPatch = { [styleKey]: expression };
        }
      }
    }
    return this.finishFlow(patch, context);
  }

  private finishFlow(
    patch: PatchResult,
    context: ExecutionContext,
  ): NodeExecutionResult {
    const nextFlows = this.getNextFlows(context);
    const runtimeSignals: RuntimeSignal[] = [];
    if (nextFlows.length === 0 && context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }
    return {
      nextFlows,
      patch,
      scheduledNodes: [],
      runtimeSignals,
    };
  }

  private toNodeTarget(value: unknown): NodePortValue | undefined {
    if (!value || typeof value !== "object") return undefined;
    const target = value as Partial<NodePortValue>;
    if (target.component !== "barLoader") return undefined;
    if (
      target.weight !== "all" &&
      (typeof target.weight !== "number" || Number.isNaN(target.weight))
    ) {
      return undefined;
    }
    if (target.part !== "disk" && target.part !== "label") {
      return undefined;
    }
    return {
      component: "barLoader",
      weight: target.weight,
      part: target.part,
    };
  }

  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }

  getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }

  private resolveCompositeBindingPatch(
    basePortId: string,
    fields: ReadonlyArray<string>,
    visiting: Set<string>,
    context: ExecutionContext,
  ): Record<string, BindingExpression> | undefined {
    const result: Record<string, BindingExpression> = {};
    const baseRaw = this.getInput(basePortId, visiting, context, true);
    const baseExpr = ensureBindingExpression(baseRaw);
    if (
      baseExpr?.kind === "literal" &&
      baseExpr.value != null &&
      typeof baseExpr.value === "object" &&
      !Array.isArray(baseExpr.value)
    ) {
      const composite = baseExpr.value as Record<string, unknown>;
      for (const field of fields) {
        const expression = ensureBindingExpression(composite[field]);
        if (expression) {
          result[field] = expression;
        }
      }
    } else if (baseExpr) {
      // Whole-port expression (e.g. nested `ifElse` on transform/location).
      // Resolved once at render time and split into per-field numbers.
      for (const field of fields) {
        result[field] = baseExpr;
      }
    } else {
      for (const field of fields) {
        const subPortId = `${basePortId}.${String(field)}`;
        const expression = ensureBindingExpression(
          this.getInput(subPortId, visiting, context, true),
        );
        if (expression) {
          result[field] = expression;
        }
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
}
